/**
 * Incoming Materials Section
 *
 * PO-centric view with table, filters, and server-side pagination.
 * POs come from SAP — no manual creation. Status is either Pending (issued) or Completed (closed).
 */

import { useEffect, useMemo, useState, useCallback, lazy, Suspense } from 'react'
import { useDispatch, useSelector, useTranslation, usePagination } from '@ufc/core'
import {
  BaseDrawer,
  Badge,
  Card,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Package,
  Skeleton,
  Button,
  Label,
  PageToolbar,
  PaginationControls,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SlidersHorizontal,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@ufc/uikit'
import {
  incomingMaterialsActions,
  selectGoodsReceipts,
  selectIncomingMaterialsSubmitting,
  selectIncomingMaterialsError,
  selectPurchaseOrders,
  selectPurchaseOrdersLoading,
  selectPoPagination,
  selectStorageLocations,
  selectSuppliers,
  selectSuppliersLoading,
  selectMaterials,
  selectMaterialsLoading,
  selectLastCreatedGoodsReceiptId,
} from '../../store'
import type {
  CreateGoodsReceiptForm,
  Material,
  PurchaseOrder,
  Supplier,
} from '../../domain/types'
const PurchaseOrderDetailDrawer = lazy(() => import('../drawers').then(m => ({ default: m.PurchaseOrderDetailDrawer })))
import { PurchaseOrdersTable } from '../components/purchase-orders-table'
import type { POTableColumnKey } from '../components/constants'

// ============================================================================
// Constants & Types
// ============================================================================

type StatusFilter = 'all' | 'pending' | 'completed'

interface PurchaseOrderFilters {
  status: StatusFilter
  supplierId: string
  materialId: string
}

const ALL_COLUMNS: POTableColumnKey[] = [
  'poNumber', 'supplier', 'factory', 'materialGroup', 'poDate',
  'status', 'actions',
]

const STATUS_FILTERS: { value: StatusFilter; labelKey: string }[] = [
  { value: 'all', labelKey: 'common.all' },
  { value: 'pending', labelKey: 'incomingMaterials.poStatus.pending' },
  { value: 'completed', labelKey: 'incomingMaterials.poStatus.completed' },
]

const FILTER_OPTION_PAGE_SIZE = 10
const FILTER_SEARCH_DEBOUNCE_MS = 400

// ============================================================================
// Helper Functions
// ============================================================================

function createDefaultPoFilters(): PurchaseOrderFilters {
  return {
    status: 'all',
    supplierId: 'all',
    materialId: 'all',
  }
}

function buildPurchaseOrderFilterPayload(filters: PurchaseOrderFilters) {
  return {
    status: filters.status === 'all' ? undefined : filters.status,
    supplierId: filters.supplierId === 'all' ? undefined : filters.supplierId,
    materialId: filters.materialId === 'all' ? undefined : filters.materialId,
  }
}

function getActivePoFilterCount(filters: PurchaseOrderFilters): number {
  return Object.values(filters).filter((value) => value !== 'all').length
}

function getMaterialLabel(material: Material): string {
  if (material.code) return `${material.code} - ${material.name}`
  return material.name
}

function getSupplierLabel(supplier: Supplier): string {
  if (supplier.code) return `${supplier.code} - ${supplier.name}`
  return supplier.name
}

function findSelectedMaterial(materials: Material[], materialId: string): Material | null {
  if (materialId === 'all') return null
  return materials.find((material) => String(material.id) === materialId) ?? null
}

function findSelectedSupplier(suppliers: Supplier[], supplierId: string): Supplier | null {
  if (supplierId === 'all') return null
  return suppliers.find((supplier) => String(supplier.id) === supplierId) ?? null
}

interface IncomingMaterialsFilterDrawerProps {
  isOpen: boolean
  filters: PurchaseOrderFilters
  suppliers: Supplier[]
  materials: Material[]
  isLoadingSuppliers: boolean
  isLoadingMaterials: boolean
  onClose: () => void
  onChange: (field: keyof PurchaseOrderFilters, value: string) => void
  onSupplierSearch: (search: string) => void
  onMaterialSearch: (search: string) => void
  onClear: () => void
}

function IncomingMaterialsFilterDrawer({
  isOpen,
  filters,
  suppliers,
  materials,
  isLoadingSuppliers,
  isLoadingMaterials,
  onClose,
  onChange,
  onSupplierSearch,
  onMaterialSearch,
  onClear,
}: IncomingMaterialsFilterDrawerProps) {
  const { t } = useTranslation()
  const [supplierSearch, setSupplierSearch] = useState('')
  const [materialSearch, setMaterialSearch] = useState('')
  const [isSupplierDropdownOpen, setIsSupplierDropdownOpen] = useState(false)
  const [isMaterialDropdownOpen, setIsMaterialDropdownOpen] = useState(false)
  const selectedSupplier = useMemo(
    () => findSelectedSupplier(suppliers, filters.supplierId),
    [suppliers, filters.supplierId],
  )
  const selectedMaterial = useMemo(
    () => findSelectedMaterial(materials, filters.materialId),
    [materials, filters.materialId],
  )
  const selectedSupplierLabel = selectedSupplier ? getSupplierLabel(selectedSupplier) : t('incomingMaterials.filters.all')
  const selectedMaterialLabel = selectedMaterial ? getMaterialLabel(selectedMaterial) : t('incomingMaterials.filters.all')

  useEffect(() => {
    if (!isOpen) return undefined

    const timeoutId = window.setTimeout(() => {
      onSupplierSearch(supplierSearch.trim())
    }, FILTER_SEARCH_DEBOUNCE_MS)

    return () => window.clearTimeout(timeoutId)
  }, [isOpen, supplierSearch, onSupplierSearch])

  useEffect(() => {
    if (!isOpen) return undefined

    const timeoutId = window.setTimeout(() => {
      onMaterialSearch(materialSearch.trim())
    }, FILTER_SEARCH_DEBOUNCE_MS)

    return () => window.clearTimeout(timeoutId)
  }, [isOpen, materialSearch, onMaterialSearch])

  const handleSupplierSelect = useCallback((value: string) => {
    onChange('supplierId', value)
    setIsSupplierDropdownOpen(false)
  }, [onChange])

  const handleMaterialSelect = useCallback((value: string) => {
    onChange('materialId', value)
    setIsMaterialDropdownOpen(false)
  }, [onChange])

  const handleClear = useCallback(() => {
    setSupplierSearch('')
    setMaterialSearch('')
    onClear()
  }, [onClear])

  return (
    <BaseDrawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      title={t('incomingMaterials.filters.title')}
      mobileWidth="100vw"
      tabletWidth="24rem"
      fixedWidth={420}
    >
      <div className="flex flex-col gap-4 p-4">
        <div className="space-y-2">
          <Label>{t('incomingMaterials.filters.status')}</Label>
          <Select value={filters.status} onValueChange={(value) => onChange('status', value)}>
            <SelectTrigger>
              <SelectValue placeholder={t('incomingMaterials.filters.status')} />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((filter) => (
                <SelectItem key={filter.value} value={filter.value}>
                  {t(filter.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>{t('incomingMaterials.filters.supplier')}</Label>
          <Popover open={isSupplierDropdownOpen} onOpenChange={setIsSupplierDropdownOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={isSupplierDropdownOpen}
                className="w-full justify-between font-normal"
              >
                <span className="truncate">{selectedSupplierLabel}</span>
                <span className="ms-2 text-xs text-muted-foreground">⌄</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  value={supplierSearch}
                  onValueChange={setSupplierSearch}
                  placeholder={t('incomingMaterials.filters.supplierSearchPlaceholder')}
                />
                <CommandList>
                  <CommandEmpty>
                    {isLoadingSuppliers ? t('incomingMaterials.filters.loading') : t('incomingMaterials.filters.noSuppliers')}
                  </CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="all" onSelect={() => handleSupplierSelect('all')}>
                      {t('incomingMaterials.filters.all')}
                    </CommandItem>
                    {suppliers.map((supplier) => (
                      <CommandItem
                        key={supplier.id}
                        value={String(supplier.id)}
                        onSelect={() => handleSupplierSelect(String(supplier.id))}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{supplier.name}</span>
                          <span className="text-xs text-muted-foreground">{supplier.code}</span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="space-y-2">
          <Label>{t('incomingMaterials.filters.material')}</Label>
          <Popover open={isMaterialDropdownOpen} onOpenChange={setIsMaterialDropdownOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={isMaterialDropdownOpen}
                className="w-full justify-between font-normal"
              >
                <span className="truncate">{selectedMaterialLabel}</span>
                <span className="ms-2 text-xs text-muted-foreground">⌄</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  value={materialSearch}
                  onValueChange={setMaterialSearch}
                  placeholder={t('incomingMaterials.filters.materialSearchPlaceholder')}
                />
                <CommandList>
                  <CommandEmpty>
                    {isLoadingMaterials ? t('incomingMaterials.filters.loading') : t('incomingMaterials.filters.noMaterials')}
                  </CommandEmpty>
                  <CommandGroup>
                    <CommandItem value="all" onSelect={() => handleMaterialSelect('all')}>
                      {t('incomingMaterials.filters.all')}
                    </CommandItem>
                    {materials.map((material) => (
                      <CommandItem
                        key={material.id}
                        value={String(material.id)}
                        onSelect={() => handleMaterialSelect(String(material.id))}
                      >
                        <div className="flex flex-col">
                          <span className="font-medium">{material.name}</span>
                          <span className="text-xs text-muted-foreground">{material.code}</span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={handleClear}>
            {t('incomingMaterials.filters.clearAll')}
          </Button>
          <Button onClick={onClose}>
            {t('incomingMaterials.filters.apply')}
          </Button>
        </div>
      </div>
    </BaseDrawer>
  )
}

// ============================================================================
// Component
// ============================================================================

export function IncomingMaterialsSection() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  // Selectors
  const receipts = useSelector(selectGoodsReceipts)

  const isLoadingPurchaseOrders = useSelector(selectPurchaseOrdersLoading)
  const isSubmitting = useSelector(selectIncomingMaterialsSubmitting)
  const error = useSelector(selectIncomingMaterialsError)
  const purchaseOrders = useSelector(selectPurchaseOrders)
  const poPagination = useSelector(selectPoPagination)
  const storageLocations = useSelector(selectStorageLocations)
  const suppliers = useSelector(selectSuppliers)
  const isLoadingSuppliers = useSelector(selectSuppliersLoading)
  const materials = useSelector(selectMaterials)
  const isLoadingMaterials = useSelector(selectMaterialsLoading)
  const lastCreatedGoodsReceiptId = useSelector(selectLastCreatedGoodsReceiptId)

  // Local state
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null)
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState<PurchaseOrderFilters>(createDefaultPoFilters)
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const filterPayload = useMemo(() => buildPurchaseOrderFilterPayload(filters), [filters])
  const activeFilterCount = useMemo(() => getActivePoFilterCount(filters), [filters])

  // Server-side pagination (URL-synced)
  const pagination = usePagination({
    mode: 'server',
    totalItems: poPagination.total,
    onPageChange: (page, pageSize) => {
      dispatch(incomingMaterialsActions.fetchPurchaseOrders({
        page,
        pageSize,
        search: searchQuery || undefined,
        ...filterPayload,
      }))
    },
  })

  const isLoading = isLoadingPurchaseOrders

  // Initial data fetch
  useEffect(() => {
    dispatch(incomingMaterialsActions.fetchStorageLocations())
    dispatch(incomingMaterialsActions.fetchSuppliers({ pageSize: FILTER_OPTION_PAGE_SIZE }))
    dispatch(incomingMaterialsActions.fetchMaterials({ pageSize: FILTER_OPTION_PAGE_SIZE }))
  }, [dispatch])

  // Reset to page 1 when filters change and re-query the backend.
  useEffect(() => {
    pagination.resetPage()
    dispatch(incomingMaterialsActions.fetchPurchaseOrders({
      page: 1,
      pageSize: pagination.pageSize,
      search: searchQuery || undefined,
      ...filterPayload,
    }))
  }, [dispatch, searchQuery, filterPayload, pagination.pageSize])

  // Close drawer and refresh data when goods receipt is created
  useEffect(() => {
    if (lastCreatedGoodsReceiptId && !isSubmitting && detailDrawerOpen) {
      setDetailDrawerOpen(false)
      setSelectedPO(null)
      dispatch(incomingMaterialsActions.fetchPurchaseOrders({
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: searchQuery || undefined,
        ...filterPayload,
      }))
      dispatch(incomingMaterialsActions.clearLastCreatedIds())
    }
  }, [lastCreatedGoodsReceiptId, isSubmitting, detailDrawerOpen, dispatch, pagination.page, pagination.pageSize, searchQuery, filterPayload])

  // Handlers
  const handleSubmitGoodsReceipt = useCallback(
    (form: CreateGoodsReceiptForm) => {
      dispatch(incomingMaterialsActions.createGoodsReceipt(form))
    },
    [dispatch]
  )

  const handlePOClick = useCallback((po: PurchaseOrder) => {
    setSelectedPO(po)
    setDetailDrawerOpen(true)
  }, [])

  const handleDetailDrawerClose = useCallback((open: boolean) => {
    setDetailDrawerOpen(open)
    if (!open) {
      setSelectedPO(null)
    }
  }, [])

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value)
  }, [])

  const handleFilterChange = useCallback((field: keyof PurchaseOrderFilters, value: string) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
    }))
  }, [])

  const handleOpenFilterDrawer = useCallback(() => {
    setIsFilterDrawerOpen(true)
  }, [])

  const handleCloseFilterDrawer = useCallback(() => {
    setIsFilterDrawerOpen(false)
  }, [])

  const handleClearFilters = useCallback(() => {
    setFilters(createDefaultPoFilters())
  }, [])

  const handleSupplierSearch = useCallback((supplierSearch: string) => {
    dispatch(
      incomingMaterialsActions.fetchSuppliers({
        search: supplierSearch || undefined,
        pageSize: FILTER_OPTION_PAGE_SIZE,
      }),
    )
  }, [dispatch])

  const handleMaterialSearch = useCallback((materialSearch: string) => {
    dispatch(
      incomingMaterialsActions.fetchMaterials({
        search: materialSearch || undefined,
        pageSize: FILTER_OPTION_PAGE_SIZE,
      }),
    )
  }, [dispatch])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Package className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {t('sidebar.incomingMaterials')}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t('incomingMaterials.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <PageToolbar
          searchPlaceholder={t('incomingMaterials.searchPOs')}
          searchValue={searchQuery}
          onSearchChange={handleSearchChange}
        >
          <Button variant="outline" className="gap-2" onClick={handleOpenFilterDrawer}>
            <SlidersHorizontal className="h-4 w-4" />
            {t('incomingMaterials.filters.title')}
            {activeFilterCount > 0 && (
              <Badge variant="secondary" className="ms-1 h-5 min-w-5 justify-center rounded-full px-1.5 text-xs">
                {activeFilterCount}
              </Badge>
            )}
          </Button>
        </PageToolbar>

        {/* Purchase Orders */}
        {isLoading ? (
          <Card className="p-4">
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-16" />
                </div>
              ))}
            </div>
          </Card>
        ) : purchaseOrders.length === 0 ? (
          <Card className="p-12">
            <div className="text-center">
              <Package className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">
                {searchQuery || activeFilterCount > 0
                  ? t('incomingMaterials.noMatchingPOs')
                  : t('incomingMaterials.noPurchaseOrders')}
              </p>
            </div>
          </Card>
        ) : (
          <PurchaseOrdersTable
            purchaseOrders={purchaseOrders}
            selectedRowId={selectedPO?.documentId ?? null}
            visibleColumns={ALL_COLUMNS}
            onRowClick={handlePOClick}
          />
        )}

        {/* Pagination */}
        <PaginationControls
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalItems={pagination.totalItems}
          totalPages={pagination.totalPages}
          onPageChange={(p) => pagination.goToPage(p)}
          onPageSizeChange={(ps) => pagination.setPageSize(ps as any)}
          showPageSizeSelector
          showItemCount
          labels={{
            showing: t('incomingMaterials.table.showing'),
            perPage: t('incomingMaterials.table.perPage'),
          }}
        />
      </div>

      <IncomingMaterialsFilterDrawer
        isOpen={isFilterDrawerOpen}
        filters={filters}
        suppliers={suppliers}
        materials={materials}
        isLoadingSuppliers={isLoadingSuppliers}
        isLoadingMaterials={isLoadingMaterials}
        onClose={handleCloseFilterDrawer}
        onChange={handleFilterChange}
        onSupplierSearch={handleSupplierSearch}
        onMaterialSearch={handleMaterialSearch}
        onClear={handleClearFilters}
      />

      {/* PO Detail Drawer (with receive goods) */}
      <Suspense fallback={null}>
        <PurchaseOrderDetailDrawer
          open={detailDrawerOpen}
          onOpenChange={handleDetailDrawerClose}
          purchaseOrder={selectedPO}
          goodsReceipts={receipts}
          storageLocations={storageLocations}
          isSubmitting={isSubmitting}
          onReceiveGoods={handleSubmitGoodsReceipt}
        />
      </Suspense>
    </div>
  )
}
